import { useQuery } from '@tanstack/react-query';
import { Loader2, Mail, MessageSquare, Users } from 'lucide-react';
import { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '../../../components/ui/card';
import { DateRangePicker } from '../../../components/ui/date-range-picker';
import { partnerApi } from '../../../lib/api';

interface EngageMetricsData {
  summary: {
    activeSmbs: number;
    platformsWithSends: number;
    emailsSent: number;
    smsSent: number;
    totalSends: number;
    changePercent: number;
    changeAmount: number;
  };
  businessSends: Array<{
    businessName: string;
    platformName: string;
    emailsSent: number;
    smsSent: number;
    totalSends: number;
  }>;
  platformSends: Array<{
    platform: string;
    activeSmbs: number;
    emailsSent: number;
    smsSent: number;
    totalSends: number;
  }>;
  sendsOverTime: Array<{
    date: string;
    sends: number;
  }>;
  uniqueSmbSendsOverTime: Array<{
    date: string;
    uniqueSmbs: number;
  }>;
  monthlyUsage: Array<{
    monthYear: string;
    smbName: string;
    activeMonth: number;
    totalMessages: number;
    totalEmails: number;
    totalSms: number;
  }>;
}

export const EngageMetrics = () => {
  const [selectedDays, setSelectedDays] = useState<number>(0); // 0 = All Time
  const [hiddenBusinesses, setHiddenBusinesses] = useState<Set<string>>(
    new Set(),
  );
  const [selectedMetric, setSelectedMetric] = useState<
    'total' | 'email' | 'sms'
  >('total');

  const {
    data: metrics,
    isLoading,
    isFetching,
  } = useQuery<EngageMetricsData>({
    queryKey: ['engage-metrics', selectedDays],
    queryFn: () => partnerApi.getEngageMetrics(selectedDays),
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: previousData => previousData,
  });

  // Separate query for all-time monthly usage data
  const { data: allTimeMetrics } = useQuery<EngageMetricsData>({
    queryKey: ['engage-metrics', 'all-time'],
    queryFn: () => partnerApi.getEngageMetrics(365), // Get 1 year of data for monthly usage
    staleTime: 10 * 60 * 1000, // 10 minutes (longer cache for all-time data)
    enabled: true, // Always fetch this
  });

  if (isLoading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading metrics...</div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`;
    }
    return num.toString();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const fillMissingDays = (
    data: Array<{ date: string; sends: number }>,
    days: number = selectedDays,
  ) => {
    if (!data || data.length === 0) return [];

    // For All Time (days <= 0), return raw data sorted by date
    if (days <= 0) {
      return [...data].sort((a, b) => a.date.localeCompare(b.date));
    }

    const result = [];
    const today = new Date();
    const dataMap = new Map(data.map(item => [item.date, item.sends]));

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      result.push({
        date: dateStr,
        sends: dataMap.get(dateStr) || 0,
      });
    }

    return result;
  };

  const fillMissingDaysForUniqueSmbs = (
    data: Array<{ date: string; uniqueSmbs: number }>,
    days: number = selectedDays,
  ) => {
    if (!data || data.length === 0) return [];

    // For All Time (days <= 0), return raw data sorted by date
    if (days <= 0) {
      return [...data].sort((a, b) => a.date.localeCompare(b.date));
    }

    const result = [];
    const today = new Date();
    const dataMap = new Map(data.map(item => [item.date, item.uniqueSmbs]));

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      result.push({
        date: dateStr,
        uniqueSmbs: dataMap.get(dateStr) || 0,
      });
    }

    return result;
  };

  const getUniqueBusinesses = (
    monthlyUsage: Array<{
      smbName: string;
      monthYear: string;
      totalEmails: number;
      totalSms: number;
    }>,
  ) => {
    return [...new Set(monthlyUsage.map(item => item.smbName))];
  };

  const getBusinessColor = (index: number) => {
    const colors = [
      '#3b82f6', // blue
      '#10b981', // emerald
      '#f59e0b', // amber
      '#ef4444', // red
      '#8b5cf6', // violet
      '#06b6d4', // cyan
      '#84cc16', // lime
      '#f97316', // orange
    ];

    return colors[index % colors.length];
  };

  const toggleBusinessVisibility = (business: string) => {
    setHiddenBusinesses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(business)) {
        newSet.delete(business);
      } else {
        newSet.add(business);
      }
      return newSet;
    });
  };

  const transformMonthlyUsageData = (
    monthlyUsage: Array<{
      smbName: string;
      monthYear: string;
      totalEmails: number;
      totalSms: number;
    }>,
    metric: 'total' | 'email' | 'sms' = 'total',
  ) => {
    const uniqueBusinesses = getUniqueBusinesses(monthlyUsage);
    const uniqueMonths = [
      ...new Set(monthlyUsage.map(item => item.monthYear)),
    ].sort((a, b) => {
      // Convert monthYear strings to Date objects for proper chronological sorting
      const dateA = new Date(a + '-01'); // Add day to make it a valid date
      const dateB = new Date(b + '-01');
      return dateA.getTime() - dateB.getTime();
    });

    return uniqueMonths.map(month => {
      const dataPoint: Record<string, string | number> = { monthYear: month };

      uniqueBusinesses.forEach(business => {
        const businessData = monthlyUsage.find(
          item => item.smbName === business && item.monthYear === month,
        );

        let value = 0;
        if (metric === 'email') {
          value = businessData?.totalEmails || 0;
        } else if (metric === 'sms') {
          value = businessData?.totalSms || 0;
        } else {
          // total
          value =
            (businessData?.totalEmails || 0) + (businessData?.totalSms || 0);
        }

        dataPoint[business] = value;
      });

      return dataPoint;
    });
  };

  return (
    <div className="space-y-6">
      <DateRangePicker
        selectedDays={selectedDays}
        onSelectDays={setSelectedDays}
        isLoading={isFetching}
      />

      {/* Summary Stats */}
      <div
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity duration-200 ${isFetching ? 'opacity-75' : 'opacity-100'}`}>
        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Active Engage SMBs
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {metrics.summary.activeSmbs}
              </p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Platforms with Sends (
                {selectedDays === 0 ? 'All Time' : `${selectedDays}d`})
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {metrics.summary.platformsWithSends}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Emails Sent (
                {selectedDays === 0 ? 'All Time' : `Last ${selectedDays}d`})
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {formatNumber(metrics.summary.emailsSent)}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Mail className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                SMS Sent (
                {selectedDays === 0 ? 'All Time' : `Last ${selectedDays}d`})
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {metrics.summary.smsSent}
              </p>
            </div>
            <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Active Engage SMBs Table */}
      {metrics.businessSends && metrics.businessSends.length > 0 && (
        <Card
          className={`p-6 transition-opacity duration-200 ${isFetching ? 'opacity-75' : 'opacity-100'}`}>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Active Engage SMBs (
              {selectedDays === 0 ? 'All Time' : `${selectedDays}d`})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SMB name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Emails sent (
                    {selectedDays === 0 ? 'All Time' : `${selectedDays}d`})
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SMS sent (
                    {selectedDays === 0 ? 'All Time' : `${selectedDays}d`})
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total sends (
                    {selectedDays === 0 ? 'All Time' : `${selectedDays}d`})
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {metrics.businessSends.map((business, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.businessName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.emailsSent.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.smsSent}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.totalSends.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Charts Row */}
      <div className="relative grid grid-cols-1 xl:grid-cols-2 gap-6">
        {isFetching && (
          <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
            <div className="flex items-center space-x-2 text-blue-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm font-medium">Updating charts...</span>
            </div>
          </div>
        )}
        {/* Email/SMS Sends Over Time Chart */}
        {metrics.sendsOverTime && metrics.sendsOverTime.length > 0 && (
          <Card
            className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Daily Email/SMS Sends
                </h3>
                <span className="text-2xl font-bold text-gray-900">
                  {formatNumber(metrics.summary.totalSends)}
                </span>
              </div>
              {selectedDays > 0 && (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-sm text-gray-500">
                    {selectedDays} days (vs previous {selectedDays} days)
                  </p>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`text-sm font-medium ${metrics.summary.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {metrics.summary.changePercent >= 0 ? '+' : ''}
                      {metrics.summary.changePercent}%
                    </span>
                    <span
                      className={`text-sm ${metrics.summary.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ({metrics.summary.changePercent >= 0 ? '+' : ''}
                      {formatNumber(metrics.summary.changeAmount)})
                    </span>
                  </div>
                </div>
              )}
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={fillMissingDays(metrics.sendsOverTime)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.375rem',
                  }}
                  labelFormatter={formatDate}
                  formatter={value => [value, 'Sends']}
                />
                <Area
                  type="monotone"
                  dataKey="sends"
                  fill="url(#colorSends)"
                  stroke="none"
                />
                <Line
                  type="monotone"
                  dataKey="sends"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={props => {
                    const { payload, index } = props;
                    if (payload.sends === 0)
                      return <circle key={index} r={0} />;
                    return (
                      <circle
                        key={index}
                        r={4}
                        fill="#3b82f6"
                        strokeWidth={2}
                      />
                    );
                  }}
                  activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                />
                <defs>
                  <linearGradient id="colorSends" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Unique SMB Sends Over Time Chart */}
        {metrics.uniqueSmbSendsOverTime &&
          metrics.uniqueSmbSendsOverTime.length > 0 && (
            <Card
              className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Daily Unique SMB Sends
                  </h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {metrics.summary.activeSmbs}
                    </span>
                    <span className="text-sm font-medium text-gray-500">
                      Active SMBs
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedDays === 0 ? 'All Time' : `${selectedDays} days`}
                </p>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart
                  data={fillMissingDaysForUniqueSmbs(
                    metrics.uniqueSmbSendsOverTime,
                  )}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                    stroke="#9ca3af"
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    stroke="#9ca3af"
                    tickFormatter={value => Math.round(value).toString()}
                    domain={[0, 'dataMax']}
                    allowDecimals={false}
                    tickCount={6}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem',
                    }}
                    labelFormatter={formatDate}
                  />
                  <Area
                    type="monotone"
                    dataKey="uniqueSmbs"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#colorUniqueSmbs)"
                    dot={props => {
                      const { payload, index } = props;
                      if (payload.uniqueSmbs === 0)
                        return <circle key={index} r={0} />;
                      return (
                        <circle
                          key={index}
                          r={4}
                          fill="#10b981"
                          strokeWidth={2}
                        />
                      );
                    }}
                    activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                  />
                  <defs>
                    <linearGradient
                      id="colorUniqueSmbs"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop
                        offset="95%"
                        stopColor="#10b981"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  </defs>
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          )}
      </div>

      {/* Engage Usage by SMB Chart - All Time */}
      {allTimeMetrics?.monthlyUsage &&
        allTimeMetrics.monthlyUsage.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Engage Usage by SMB (All Time)
              </h3>
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="metric-select"
                  className="text-sm font-medium text-gray-700">
                  Show:
                </label>
                <select
                  id="metric-select"
                  value={selectedMetric}
                  onChange={e =>
                    setSelectedMetric(
                      e.target.value as 'total' | 'email' | 'sms',
                    )
                  }
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="total">Total Messages</option>
                  <option value="email">Email Only</option>
                  <option value="sms">SMS Only</option>
                </select>
              </div>
            </div>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={transformMonthlyUsageData(
                    allTimeMetrics.monthlyUsage,
                    selectedMetric,
                  )}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="monthYear"
                    tick={{ fontSize: 12 }}
                    stroke="#9ca3af"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    stroke="#9ca3af"
                    tickFormatter={value => formatNumber(value)}
                    label={{
                      value:
                        selectedMetric === 'email'
                          ? 'Emails'
                          : selectedMetric === 'sms'
                            ? 'SMS'
                            : 'Messages',
                      angle: -90,
                      position: 'insideLeft',
                      style: {
                        textAnchor: 'middle',
                        fontSize: '12px',
                        fill: '#6b7280',
                      },
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem',
                    }}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length > 0) {
                        // Filter out zero values
                        const filteredPayload = payload.filter(
                          item => Number(item.value) > 0,
                        );

                        if (filteredPayload.length === 0) {
                          return null;
                        }

                        return (
                          <div className="bg-white border border-gray-200 rounded-md p-3 shadow-lg">
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              {label}
                            </p>
                            {filteredPayload.map((entry, index) => (
                              <p key={index} className="text-sm text-gray-600">
                                <span
                                  className="inline-block w-3 h-3 rounded-full mr-2"
                                  style={{ backgroundColor: entry.color }}
                                />
                                {entry.name}:{' '}
                                {formatNumber(Number(entry.value))}{' '}
                                {selectedMetric === 'email'
                                  ? 'emails'
                                  : selectedMetric === 'sms'
                                    ? 'SMS'
                                    : 'messages'}
                              </p>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {getUniqueBusinesses(allTimeMetrics.monthlyUsage)
                    .filter(business => !hiddenBusinesses.has(business))
                    .map(business => {
                      const originalIndex = getUniqueBusinesses(
                        allTimeMetrics.monthlyUsage,
                      ).indexOf(business);
                      return (
                        <Line
                          key={business}
                          type="monotone"
                          dataKey={business}
                          stroke={getBusinessColor(originalIndex)}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          name={business}
                          connectNulls={false}
                          activeDot={{
                            r: 5,
                            stroke: getBusinessColor(originalIndex),
                            strokeWidth: 2,
                          }}
                        />
                      );
                    })}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Custom Legend with Checkboxes */}
            <div className="mt-4">
              <div className="flex flex-wrap gap-3">
                {getUniqueBusinesses(allTimeMetrics.monthlyUsage).map(
                  (business, index) => {
                    const isHidden = hiddenBusinesses.has(business);
                    return (
                      <label
                        key={business}
                        className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={!isHidden}
                          onChange={() => toggleBusinessVisibility(business)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: getBusinessColor(index) }}
                          />
                          <span
                            className={`text-sm ${isHidden ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                            {business}
                          </span>
                        </div>
                      </label>
                    );
                  },
                )}
              </div>
            </div>
          </Card>
        )}
    </div>
  );
};
